import React from 'react';
import classnames from 'classnames';
import { Icon, useClickOutside } from '~/ui';
import { IconSize } from '~/ui/components/Icon/IconSizes';
import './DropDownMenu.scss';

type IconProps = React.ComponentProps<typeof Icon>;

interface DropDownMenuProps {
    title?: {
        name?: string | React.ReactNode;
        icon?: IconProps['icon'];
        iconColor?: string;
    };
    placeholder?: string;
    styles?: Record<string, string>;
    isOpen?: boolean;
    toggle?: () => void;
    close?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    icon?: IconProps['icon'];
    position?: 'down' | 'down-right' | 'left';
    customDropDownButton?: React.ComponentType;
    children?: React.ReactNode;
    'data-testid'?: string;
}

export const menuTitleTextDataTestIdSuffix = 'menutitle-text';

const DropDownMenu = React.forwardRef<HTMLDivElement, DropDownMenuProps>(
    function DropDownMenu(
        {
            title,
            placeholder,
            styles,
            children,
            isOpen,
            toggle,
            close,
            onMouseEnter,
            onMouseLeave,
            icon,
            position = 'down',
            customDropDownButton,
            ...extra
        },
        ref
    ) {
        const dropdownStyles = styles || {
            innerContainer: 'dropdown-flex',
            menuTitle: 'dropdown-flex-item',
            menuTitleText: ''
        };

        const menuTitle = title || {};
        let dropDownItems = 'dropdown-hide-items';
        let rotate = '';

        if (isOpen) {
            dropDownItems = classnames(
                dropdownStyles.menuItems,
                position,
                'dropdown-show-items'
            );
            rotate = '_t-rotate-180';
        }

        useClickOutside(ref, close);

        const DropDownButtonComponent = customDropDownButton;

        return (
            <div
                className={`dropdown _p-relative _d-inline-block ${dropdownStyles.outerContainer}`}
                ref={ref}
                data-testid={extra['data-testid'] || 'dropdown'}
            >
                <div
                    className={dropdownStyles.innerContainer}
                    onClick={toggle}
                    onKeyDown={toggle}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    role="button"
                    tabIndex={0}
                    data-testid={
                        extra['data-testid']
                            ? `${extra['data-testid']}-button`
                            : 'dropdown-button'
                    }
                >
                    {DropDownButtonComponent ? (
                        <DropDownButtonComponent />
                    ) : (
                        <div
                            className={dropdownStyles.menuTitle}
                            data-testid={
                                extra['data-testid']
                                    ? `${extra['data-testid']}-menutitle`
                                    : 'dropdown-menutitle'
                            }
                        >
                            {menuTitle.icon && (
                                <Icon
                                    className="dropdown-icon"
                                    icon={menuTitle.icon}
                                    color={menuTitle.iconColor}
                                    data-testid={
                                        extra['data-testid']
                                            ? `${extra['data-testid']}-menutitle-icon`
                                            : 'dropdown-menutitle-icon'
                                    }
                                />
                            )}
                            {placeholder && !menuTitle.name && (
                                <span
                                    className="dropdown--placeholder"
                                    data-testid="dropdown-placeholder"
                                >
                                    {placeholder}
                                </span>
                            )}
                            {menuTitle.name && (
                                <span
                                    className={dropdownStyles.menuTitleText}
                                    data-testid={
                                        extra['data-testid']
                                            ? `${extra['data-testid']}-${menuTitleTextDataTestIdSuffix}`
                                            : 'dropdown-menutitle-text'
                                    }
                                >
                                    {menuTitle.name}
                                </span>
                            )}
                        </div>
                    )}
                    <div>
                        {icon !== undefined && (
                            <Icon
                                className={`${rotate} ${dropdownStyles.icon}`}
                                icon={icon}
                                color={dropdownStyles.dropdownToggleColor}
                                size={
                                    (dropdownStyles.iconSize as IconSize) || 'm'
                                }
                                data-testid={
                                    extra['data-testid']
                                        ? `${extra['data-testid']}-icon`
                                        : 'dropdown-icon'
                                }
                            />
                        )}
                    </div>
                </div>
                <div
                    data-testid={
                        extra['data-testid']
                            ? `${extra['data-testid']}-items`
                            : 'dropdown-items'
                    }
                    className={dropDownItems}
                >
                    {children}
                </div>
            </div>
        );
    }
);

export default DropDownMenu;
